"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get AddSubjectsToReviewCycleRequest () {
        return AddSubjectsToReviewCycleRequest;
    },
    get BulkUpdateReviewCycleReportsRequest () {
        return BulkUpdateReviewCycleReportsRequest;
    },
    get BulkUpdateReviewCycleReportsUpdates () {
        return BulkUpdateReviewCycleReportsUpdates;
    },
    get CreateAutoflowConfig () {
        return CreateAutoflowConfig;
    },
    get CreateAutomatedReviewCycleRequest () {
        return CreateAutomatedReviewCycleRequest;
    },
    get CreateGroupReviewCycleRequest () {
        return CreateGroupReviewCycleRequest;
    },
    get CreateManualReviewCycleRequest () {
        return CreateManualReviewCycleRequest;
    },
    get CreateReviewCycleRequest () {
        return CreateReviewCycleRequest;
    },
    get GetReviewCyclePreviewSubjectsResultsParams () {
        return GetReviewCyclePreviewSubjectsResultsParams;
    },
    get GetReviewCyclePreviewSubjectsResultsRequest () {
        return GetReviewCyclePreviewSubjectsResultsRequest;
    },
    get GetReviewCyclesQueryParams () {
        return GetReviewCyclesQueryParams;
    },
    get PostReviewCyclePreviewSubjectsRequest () {
        return PostReviewCyclePreviewSubjectsRequest;
    },
    get PreviewSubjectsSortDirection () {
        return PreviewSubjectsSortDirection;
    },
    get PreviewSubjectsSortName () {
        return PreviewSubjectsSortName;
    },
    get UpdateAutoflowConfig () {
        return UpdateAutoflowConfig;
    },
    get UpdateReviewCycleRequest () {
        return UpdateReviewCycleRequest;
    }
});
const _zod = require("zod");
const _models = require("@performyard/models");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _formassignment = require("../form-assignment");
const _reviewcycle = require("./review-cycle");
const GetReviewCyclesQueryParams = _zod.z.object({
    search: _zod.z.string().optional(),
    status: _zod.z.union([
        _zod.z.array(_reviewcycle.ReviewCycleStatus),
        _reviewcycle.ReviewCycleStatus.transform((s)=>[
                s
            ])
    ]).optional(),
    type: _reviewcycle.ReviewCycleType.optional(),
    easyAddEnabled: _zod.z.enum([
        "true",
        "false"
    ]).transform((v)=>v === "true").optional(),
    sortName: _zod.z.enum([
        "name",
        "startDate",
        "status"
    ]).default("startDate").transform((v)=>{
        const map = {
            name: "name",
            startDate: "start_date",
            status: "status"
        };
        return map[v];
    }),
    sortDirection: _zod.z.enum([
        "asc",
        "desc"
    ]).default("desc"),
    skip: _zod.z.coerce.number().int().nonnegative().default(0),
    limit: _zod.z.coerce.number().int().min(1).max(50).default(50)
});
const UpdateAutoflowConfig = _zod.z.object({
    enabled: _zod.z.boolean().optional(),
    type: _zod.z.enum([
        "rolling",
        "one-time"
    ]).optional(),
    endDate: _utilsisomorphic.transformDate.nullable().optional(),
    userQuery: _models.CohortQueryModel.optional()
});
const CreateAutoflowConfig = _zod.z.object({
    type: _zod.z.enum([
        "rolling",
        "one-time"
    ]),
    endDate: _utilsisomorphic.transformDate.nullable().optional(),
    userQuery: _models.CohortQueryModel
}).refine((data)=>data.type === "rolling" || !data.endDate, {
    message: "endDate is only applicable to rolling autoflows"
});
const UpdateReviewCycleRequest = _zod.z.object({
    name: _zod.z.string().optional(),
    startDate: _utilsisomorphic.transformDate.optional(),
    adminsOnly: _zod.z.boolean().optional(),
    autoflow: UpdateAutoflowConfig.optional(),
    formChanges: _reviewcycle.FormChange.array().optional()
}).refine((data)=>Object.keys(data).length > 0, {
    message: "Request body must contain at least one field to update"
});
// ============================================================================
// POST /review-cycles Request Body
// ============================================================================
const MAX_FORMS_PER_TYPE = 100;
const CreateReviewCycleBase = _zod.z.object({
    name: _zod.z.string().min(1, "Cycle name is required"),
    workflowId: _utilsisomorphic.transformObjectId,
    startDate: _zod.z.coerce.date(),
    userSelectionId: _utilsisomorphic.transformObjectId,
    formAssignments: _zod.z.array(_zod.z.union([
        _formassignment.DirectFormAssignmentInput,
        _formassignment.EasyAddFormConfigInput
    ])).min(1, "At least one form assignment is required").refine((assignments)=>assignments.filter((a)=>"basic" in a).length <= MAX_FORMS_PER_TYPE, `Maximum ${MAX_FORMS_PER_TYPE} standard form assignments allowed`).refine((assignments)=>assignments.filter((a)=>"easyAdd" in a).length <= MAX_FORMS_PER_TYPE, `Maximum ${MAX_FORMS_PER_TYPE} 360 form assignments allowed`).refine((assignments)=>assignments.filter((a)=>"external" in a).length <= MAX_FORMS_PER_TYPE, `Maximum ${MAX_FORMS_PER_TYPE} external form assignments allowed`),
    adminsOnly: _zod.z.boolean().optional().default(false)
});
const CreateManualReviewCycleRequest = CreateReviewCycleBase.extend({
    type: _zod.z.literal("manual")
});
const CreateGroupReviewCycleRequest = CreateReviewCycleBase.extend({
    type: _zod.z.literal("group"),
    departmentId: _utilsisomorphic.transformObjectId
});
const CreateAutomatedReviewCycleRequest = CreateReviewCycleBase.omit({
    userSelectionId: true
}).extend({
    type: _zod.z.literal("automated"),
    autoflow: CreateAutoflowConfig
});
const CreateReviewCycleRequest = _zod.z.discriminatedUnion("type", [
    CreateManualReviewCycleRequest,
    CreateGroupReviewCycleRequest,
    CreateAutomatedReviewCycleRequest
]);
const AddSubjectsToReviewCycleRequest = _zod.z.object({
    userSelectionId: _utilsisomorphic.transformObjectId,
    formAssignments: _zod.z.array(_formassignment.DirectFormAssignmentInput).min(1, "At least one form assignment is required"),
    startDate: _zod.z.coerce.date().optional(),
    adminsOnly: _zod.z.boolean().optional()
});
const PreviewSubjectsSortName = _zod.z.enum([
    "name",
    "manager",
    "group",
    "hireDate"
]);
const PreviewSubjectsSortDirection = _zod.z.enum([
    "asc",
    "desc"
]);
const PostReviewCyclePreviewSubjectsRequest = _zod.z.object({
    userQuery: _models.CohortQueryModel,
    cycleId: _utilsisomorphic.transformObjectId.optional()
});
const GetReviewCyclePreviewSubjectsResultsParams = _zod.z.object({
    previewId: _utilsisomorphic.transformObjectId
});
const GetReviewCyclePreviewSubjectsResultsRequest = _zod.z.object({
    search: _zod.z.string().trim().optional(),
    sortName: PreviewSubjectsSortName.default("name"),
    sortDirection: PreviewSubjectsSortDirection.default("asc"),
    skip: _zod.z.coerce.number().int().nonnegative().default(0),
    limit: _zod.z.coerce.number().int().min(1).max(100).default(50)
});
const BulkUpdateReviewCycleReportsUpdates = _zod.z.object({
    visible: _zod.z.boolean().optional(),
    redacted: _zod.z.boolean().optional(),
    authorsDueDate: _utilsisomorphic.transformDate.optional(),
    signersDueDate: _utilsisomorphic.transformDate.optional()
}).refine((d)=>Object.keys(d).length > 0, {
    message: "At least one update field is required"
}).refine((d)=>d.authorsDueDate === undefined || d.signersDueDate === undefined || d.signersDueDate >= d.authorsDueDate, {
    message: "Signer's due date must be equal to or after the author's due date"
});
const BulkUpdateReviewCycleReportsRequest = _zod.z.object({
    reviewReportIds: _zod.z.array(_utilsisomorphic.transformObjectId).min(1),
    updates: BulkUpdateReviewCycleReportsUpdates
});
